import { CalendarPlus, ImageIcon, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { LGoButton } from "@/ui/branded";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/primitives/dropdown-menu";
import type { Activity } from "@/domain/activity/types";

function formatPrice(price: number | null): string {
  if (price === null) return "Cena neuvedena";
  return `${price.toLocaleString("cs-CZ")} Kč`;
}

function activityDetails(activity: Activity): string {
  return [
    activity.subcategory?.category?.name,
    activity.subcategory?.displayName,
    formatPrice(activity.priceOriginal),
  ]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
}

interface ActivityRowProps {
  activity: Activity;
  onPublishOffer: (activity: Activity) => void;
  onEdit: (activity: Activity) => void;
  onDelete: (activity: Activity) => void;
}

export function ActivityRow({
  activity,
  onPublishOffer,
  onEdit,
  onDelete,
}: ActivityRowProps) {
  return (
    <li className="flex flex-col gap-2 py-4 md:flex-row md:items-start md:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        {activity.imageUrl ? (
          <img
            src={activity.imageUrl}
            alt=""
            className="h-10 w-10 shrink-0 rounded-md object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <ImageIcon className="h-4 w-4" aria-hidden="true" />
          </div>
        )}
        <div className="min-w-0 space-y-1">
          <p className="font-medium truncate">{activity.name}</p>
          <p className="text-sm text-muted-foreground">{activityDetails(activity)}</p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <LGoButton
          variant="ghost"
          iconLeft={<CalendarPlus />}
          onClick={() => onPublishOffer(activity)}
        >
          Vypsat termín
        </LGoButton>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <LGoButton variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">Otevřít menu</span>
            </LGoButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(activity)}>
              <Pencil className="mr-2 h-4 w-4" />
              Upravit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(activity)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Smazat
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}
